// ============================================================================
// PairsForm — Parcours certifiés : SOURCE UNIQUE (libellés, couleurs, files,
// voies, parcours e-learning, codes du répertoire, vocabulaire des modules)
// ============================================================================
// Référentiel : docs/CERTIFICATIONS.md (strate 2, règle dure). Plan :
// docs/plans/PLAN_CERTIFICATIONS_NIVEAUX.md (lot 1, GO FX 2026-10-06).
//
// Trois objets, trois vocabulaires, jamais mélangés :
//   - la FORMATION qu'on suit    → Module Vert / Module Rouge (nom de produit,
//     ne qualifie JAMAIS une personne) ;
//   - la CERTIFICATION qu'on obtient → son titre officiel, qui dépend de la VOIE ;
//   - le NIVEAU qu'on montre     → libellé court, unisexe, + étoiles + badge.
//
// Aucun écran, aucun service, aucun document n'écrit ces libellés, couleurs
// ou ordres en dur. Avant d'en ajouter un : le décider dans
// docs/CERTIFICATIONS.md, puis l'ajouter ICI. L'audit du 2026-10-06 a trouvé
// 27 tables parallèles (19 client, 8 serveur) : elles ont été supprimées.
// ============================================================================
/** Les trois niveaux, du plus bas au plus haut (ordre des étoiles). */
export const NIVEAUX_ANIMATEUR = [
    "CERTIFICATION",
    "ACCREDITATION",
    "EXPERT_FORMATEUR",
];
/** Ordre de service et d'affichage des files : noir > rouge > vert. */
export const TIERS = ["NOIR", "ROUGE", "VERT"];
export const NIVEAUX = {
    CERTIFICATION: {
        court: "Certification Animation",
        etoiles: 1,
        badge: "/badge-certification-animation.png",
        couleur: "success",
        hex: { fg: "#1B5E20", bg: "#E8F5E9", border: "#81C784" },
        couleurNom: "vert",
        libelleBadge: "Badge vert",
        tier: "VERT",
        lettre: "V",
        parcours: ["VERT"],
        description: "Atteste la capacité à animer des groupes de codéveloppement.",
        estCertifiant: true,
    },
    ACCREDITATION: {
        court: "Accréditation Intervention",
        etoiles: 2,
        badge: "/badge-certification-intervention.png",
        couleur: "error",
        hex: { fg: "#B71C1C", bg: "#FDECEA", border: "#E57373" },
        couleurNom: "rouge",
        libelleBadge: "Badge rouge",
        tier: "ROUGE",
        lettre: "R",
        parcours: ["VERT", "ROUGE"],
        description: "Délivrée par Cecodev à l'issue du Module Rouge : animer des groupes et piloter des projets.",
        estCertifiant: true,
    },
    EXPERT_FORMATEUR: {
        court: "Formateur expert",
        etoiles: 3,
        // Visuel fourni par FX le 2026-08-19. Trois étoiles comme les deux autres,
        // mais HORS annuaire : `estCertifiant: false` — avoir un visuel ne fait
        // pas d'un statut interne une certification.
        badge: "/badge-formation.png",
        couleur: "default",
        hex: { fg: "#263238", bg: "#ECEFF1", border: "#78909C" },
        couleurNom: "noir",
        libelleBadge: "Badge noir",
        tier: "NOIR",
        lettre: "N",
        parcours: ["VERT", "ROUGE"],
        description: "Statut interne de formateur d'animateurs — ce n'est pas une certification.",
        estCertifiant: false,
    },
};
/** Titre officiel de la certification d'animation (FX 2026-10-06 : « tu as le
 *  bon libellé exact »). La version intra ajoute « intra-organisation ». */
const TITRE_CERTIFICATION_ANIMATION = "Animer des groupes de Codéveloppement professionnel et managérial";
export const VOIES = {
    RS7137: {
        titre: TITRE_CERTIFICATION_ANIMATION,
        court: "Examen RS 7137",
        voie: "Certification RS 7137 (Répertoire Spécifique) — journée d'examen : séance observée, entretien, cas pratique",
        niveau: "CERTIFICATION",
        accrochable: true,
        parcours: "VERT",
        slug: "RS7137",
    },
    BADGE_VERT_DS: {
        titre: `${TITRE_CERTIFICATION_ANIMATION} intra-organisation`,
        court: "Débriefing de séance (intra)",
        voie: "Certification Cecodev intra-organisation — débriefing écrit d'une séance réelle (DS)",
        niveau: "CERTIFICATION",
        accrochable: false,
        parcours: "VERT",
        slug: "Intra",
    },
    ACCREDITATION: {
        titre: "Animer des groupes et piloter des projets",
        court: "Accréditation du Cecodev",
        voie: "Accréditation du Cecodev — à l'issue du Module Rouge : débriefing de séance et journée d'examen, un jury",
        niveau: "ACCREDITATION",
        accrochable: false,
        parcours: "ROUGE",
        slug: "Accreditation",
    },
};
export const VOIES_CERTIFICATION = [
    "RS7137",
    "BADGE_VERT_DS",
    "ACCREDITATION",
];
export const CODES_RS = [
    {
        code: "RS2809",
        titre: "Certification Niveau 1 - Pratique de base de l'animation de groupes de Codéveloppement professionnel et managérial",
        du: "2017-05-24",
        au: "2021-12-31",
    },
    {
        code: "RS6136",
        titre: TITRE_CERTIFICATION_ANIMATION,
        du: "2022-10-25",
        au: "2024-10-24",
    },
    {
        code: "RS7137",
        titre: TITRE_CERTIFICATION_ANIMATION,
        du: "2025-05-01",
        au: null,
    },
];
/** Code en vigueur aujourd'hui (dernier de la liste sans date de fin). */
export const CODE_RS_ACTUEL = CODES_RS[CODES_RS.length - 1].code;
/** Code du répertoire en vigueur à une date (AAAA-MM-JJ) ; null dans un trou
 *  ou avant le premier enregistrement. */
export function codeRsPourDate(dateIso) {
    if (!dateIso)
        return null;
    const d = dateIso.slice(0, 10);
    for (const p of CODES_RS) {
        if (d >= p.du && (p.au === null || d <= p.au))
            return p.code;
    }
    return null;
}
// ----------------------------------------------------------------------------
// Formations (ce qu'on suit) — vocabulaire des produits Inter
// ----------------------------------------------------------------------------
/**
 * Les types Inter d'un produit — SOURCE UNIQUE de la liste (validateurs Zod,
 * filtres, menus) ; l'enum Drizzle `produit_inter_type` porte les mêmes
 * valeurs (0305). EXAMEN_CERTIFICATION (lot O.6, Q3 FX 2026-10-28) = le
 * passage de l'examen RS 7137 vendu à part, réservé aux parcours AJOURNE de
 * l'annuaire : régime STAGE à paiement unique, sans code d'accès.
 */
export const TYPES_PRODUIT_INTER = [
    "CODEV_INTER",
    "SUPERVISION",
    "WEBINAIRE",
    "MODULE_VERT",
    "MODULE_ROUGE",
    "MASTERCLASS",
    "EXAMEN_CERTIFICATION",
];
export function estTypeProduitInter(v) {
    return typeof v === "string" && TYPES_PRODUIT_INTER.includes(v);
}
/** Libellé public du type de produit — titres, chips, filtres. */
export const LIBELLE_TYPE_INTER = {
    CODEV_INTER: "Codéveloppement",
    SUPERVISION: "Supervision",
    WEBINAIRE: "Webinaire",
    MASTERCLASS: "Masterclass",
    MODULE_VERT: "Module Vert",
    MODULE_ROUGE: "Module Rouge",
    EXAMEN_CERTIFICATION: "Examen de certification",
};
export function libelleTypeInter(typeInter) {
    if (!typeInter)
        return "";
    return LIBELLE_TYPE_INTER[typeInter] ?? typeInter;
}
export const MODULES = {
    MODULE_VERT: {
        libelle: "Module Vert",
        parcours: "VERT",
        prepare: "CERTIFICATION",
        voies: ["RS7137", "BADGE_VERT_DS"],
        hex: NIVEAUX.CERTIFICATION.hex,
    },
    MODULE_ROUGE: {
        libelle: "Module Rouge",
        parcours: "ROUGE",
        prepare: "ACCREDITATION",
        voies: ["ACCREDITATION"],
        hex: NIVEAUX.ACCREDITATION.hex,
    },
};
export function estModuleFormation(typeInter) {
    return typeInter === "MODULE_VERT" || typeInter === "MODULE_ROUGE";
}
// ----------------------------------------------------------------------------
// Comparateurs et helpers
// ----------------------------------------------------------------------------
function estNiveau(n) {
    return !!n && Object.prototype.hasOwnProperty.call(NIVEAUX, n);
}
/** Le plus élevé de plusieurs niveaux — comparateur OFFICIEL (FX 2026-09-18 :
 *  « une seule couleur de badge par animateur »). L'ordre est celui des
 *  étoiles du référentiel : EXPERT_FORMATEUR ★★★ > ACCREDITATION ★★☆ >
 *  CERTIFICATION ★☆☆. Valeurs inconnues/nulles ignorées ; null si aucune. */
export function niveauMax(...niveaux) {
    let best = null;
    for (const n of niveaux) {
        if (!estNiveau(n))
            continue;
        if (!best || NIVEAUX[n].etoiles > NIVEAUX[best].etoiles)
            best = n;
    }
    return best;
}
/** `a` vaut-il au moins `b` dans l'ordre des étoiles ? (null = aucun badge) */
export function niveauAuMoins(a, b) {
    return estNiveau(a) && NIVEAUX[a].etoiles >= NIVEAUX[b].etoiles;
}
/** Libellé court d'un niveau, sûr face à une valeur inconnue (données legacy). */
export function libelleNiveau(niveau) {
    if (!niveau)
        return "Non défini";
    return estNiveau(niveau) ? NIVEAUX[niveau].court : niveau;
}
/** « Badge vert » — la couleur seule. */
export function libelleBadge(niveau) {
    return estNiveau(niveau) ? NIVEAUX[niveau].libelleBadge : "Sans badge";
}
/** Libellé de badge des DOCUMENTS contractuels (lettre de mission, contrat-cadre) :
 *  « Vert (Niveau 1) — Certification Animation ». */
export function libelleBadgeDocument(niveau) {
    if (!estNiveau(niveau))
        return "—";
    const n = NIVEAUX[niveau];
    const couleur = n.couleurNom.charAt(0).toUpperCase() + n.couleurNom.slice(1);
    return `${couleur} (Niveau ${n.etoiles}) — ${n.court}`;
}
/** Couleurs d'un niveau ; gris neutre pour une valeur inconnue ou absente. */
export const COULEURS_SANS_BADGE = { fg: "#546E7A", bg: "#F5F5F5", border: "#CFD8DC" };
export function couleursNiveau(niveau) {
    return estNiveau(niveau) ? NIVEAUX[niveau].hex : COULEURS_SANS_BADGE;
}
/** « ★★☆ » — représentation textuelle, utile hors React (emails, documents). */
export function etoiles(niveau) {
    const n = estNiveau(niveau) ? NIVEAUX[niveau].etoiles : 0;
    return "★".repeat(n) + "☆".repeat(Math.max(0, 3 - n));
}
/** Numéro de niveau des documents (« Niveau 1 / 2 / 3 »), "" si inconnu. */
export function numeroNiveau(niveau) {
    return estNiveau(niveau) ? String(NIVEAUX[niveau].etoiles) : "";
}
/** Rang d'affichage « vitrine » : noir 0 > rouge 1 > vert 2 ; 3 = sans badge. */
export function ordreVitrine(niveau) {
    return estNiveau(niveau) ? 3 - NIVEAUX[niveau].etoiles : 3;
}
/** File de badge d'un animateur (null = pas de badge, donc pas de file). */
export function tierDeNiveau(niveau) {
    return estNiveau(niveau) ? NIVEAUX[niveau].tier : null;
}
/** Niveau porté par une file (un seul niveau par couleur). */
export function niveauDuTier(tier) {
    for (const n of NIVEAUX_ANIMATEUR)
        if (NIVEAUX[n].tier === tier)
            return n;
    /* istanbul ignore next — les trois files sont couvertes */
    return "CERTIFICATION";
}
/** Initiale du code de groupe de la file (« N », « R », « V »). */
export const LETTRE_TIER = {
    NOIR: NIVEAUX.EXPERT_FORMATEUR.lettre,
    ROUGE: NIVEAUX.ACCREDITATION.lettre,
    VERT: NIVEAUX.CERTIFICATION.lettre,
};
/** « file noire / rouge / verte » — libellé de la file (minuscule, à composer). */
export const LIBELLE_FILE_TIER = {
    NOIR: "file noire",
    ROUGE: "file rouge",
    VERT: "file verte",
};
/** « File noire » — libellé de titre de lane. */
export function libelleFile(tier) {
    const l = LIBELLE_FILE_TIER[tier];
    return l.charAt(0).toUpperCase() + l.slice(1);
}
/** Couleurs d'une file (= celles de son badge). */
export function couleursTier(tier) {
    return NIVEAUX[niveauDuTier(tier)].hex;
}
/** Nom de la couleur d'une file (« noir », « rouge », « vert »). */
export function couleurNomTier(tier) {
    return NIVEAUX[niveauDuTier(tier)].couleurNom;
}
/** Le badge ouvre-t-il ce parcours e-learning ? (rouge ⇒ vert ; noir ⇒ tout) */
export function badgeOuvreParcours(niveau, parcours) {
    return estNiveau(niveau) && NIVEAUX[niveau].parcours.includes(parcours);
}
/** Titre officiel d'une voie ; repli sur le libellé du niveau quand la voie
 *  n'est pas renseignée (fiches d'annuaire importées avant la migration 0193). */
export function titreCertification(voie, niveau) {
    if (voie && Object.prototype.hasOwnProperty.call(VOIES, voie)) {
        return VOIES[voie].titre;
    }
    return libelleNiveau(niveau);
}
/** Libellé court d'une voie (« Examen RS 7137 »), sûr face à une valeur inconnue. */
export function libelleVoie(voie) {
    if (!voie)
        return "À qualifier";
    return Object.prototype.hasOwnProperty.call(VOIES, voie)
        ? VOIES[voie].court
        : voie;
}
/** Niveau conféré par une voie (type d'inscription à l'annuaire). */
export function niveauDeLaVoie(voie) {
    if (voie && Object.prototype.hasOwnProperty.call(VOIES, voie)) {
        return VOIES[voie].niveau;
    }
    return null;
}
/** Garde de type : la valeur est une voie connue. */
export function estVoieCertification(voie) {
    return typeof voie === "string" && Object.prototype.hasOwnProperty.call(VOIES, voie);
}
/** Référentiel de compétences évalué par une voie (lot 4) ; RS 7137 par défaut. */
export function parcoursDeLaVoie(voie) {
    return estVoieCertification(voie) ? VOIES[voie].parcours : "VERT";
}
/** Fragment de nom de fichier d'une voie (« PV_RS7137_… », « Bilan_Accreditation_… »). */
export function slugVoie(voie) {
    return estVoieCertification(voie) ? VOIES[voie].slug : VOIES.RS7137.slug;
}
/**
 * Voie de certification d'un produit : le DRAPEAU `certification_delivree`
 * décide, seul (lot O.6, décision FX 4 du 2026-10-28 : trois Modules Vert —
 * « MV » sans certification, « MVRS » avec l'examen RS 7137 inclus, « MVDS »
 * avec le débriefing de séance inclus — et le Module Rouge avec
 * l'accréditation). Le mode du groupe ne joue plus : un MVDS vendu par le hub
 * Formation (mode INTER) donne la voie DS, un MV sans drapeau n'est pas
 * certifiable même en INTRA. Le drapeau se lit EFFECTIF (produit, sinon
 * master `standards` vivant) côté serveur — `moduleDuProduit`.
 * `null` = le produit ne prépare à rien.
 */
export function voieDuProduit(certificationDelivree) {
    return estVoieCertification(certificationDelivree) ? certificationDelivree : null;
}
/** Alias historique (lot 4) : la voie proposée à une session créée depuis un groupe. */
export function voieProposeePourGroupe(groupe) {
    return voieDuProduit(groupe.certificationDelivree);
}
/** Du plus bas au plus haut (ordre de promotion). */
export const STATUTS_PARCOURS = ["FORME", "AJOURNE", "CERTIFIE"];
export function estStatutParcours(v) {
    return v === "CERTIFIE" || v === "AJOURNE" || v === "FORME";
}
export function rangStatutParcours(statut) {
    return STATUTS_PARCOURS.indexOf(statut);
}
export const STATUTS_PARCOURS_LIBELLES = {
    CERTIFIE: {
        court: "Certifié·e",
        long: (type) => (type === "ACCREDITATION" ? "Accréditation obtenue" : "Certification obtenue"),
        couleur: "success",
    },
    AJOURNE: {
        court: "Ajourné·e",
        long: (type) => (type === "ACCREDITATION" ? "Ajourné·e à l'accréditation" : "Ajourné·e à la certification"),
        couleur: "warning",
    },
    FORME: {
        court: "Formation suivie",
        long: (type) => (type === "ACCREDITATION" ? "Module Rouge suivi, sans résultat d'examen connu" : "Module Vert suivi, sans résultat d'examen connu"),
        couleur: "default",
    },
};
export function libelleStatutParcours(statut, type) {
    const l = STATUTS_PARCOURS_LIBELLES[statut];
    return type ? l.long(type) : l.court;
}
/** Parcours e-learning ouvert par une ligne d'annuaire, quel que soit son statut. */
export function parcoursDuNiveauCertification(type) {
    return NIVEAUX[type].parcours;
}
//# sourceMappingURL=certifications.js.map